import type {
  Material,
  MaterialComponent,
  MaterialRouting,
} from '@/features/match-maker/utils/match-maker.types';
import { ReportStatus } from '../enums';

export enum ProductionLineStatusEnum {
  REPORT_PENDING = 'REPORT_PENDING',
  REPORTED = 'REPORTED',
  POSTED_TO_SAP = 'POSTED_TO_SAP',
  POSTING_FAILED = 'POSTING_FAILED',
}

export type ProductionLine = {
  id: string;
  facility_code: string;
  facility_description: string;
  date: string;
  status: ProductionLineStatusEnum;
};

export type ProductionPeriod = {
  id: string;
  facility_id: string;
  production_order_id: string;
  material_id: string;
  material_code: string;
  material_description: string;
  production_order: string;
  date: string;
  start_time: string;
  end_time: string;
  status: ReportStatus;
  created_by: string;
  created_at: string;
  updated_at: string;
};

export type ProductionPeriodListResponse = {
  items: ProductionPeriod[];
  total: number;
};

export type ProductionPeriodFormSubmitBase = {
  startTime: string;
  endTime: string;
  is_24_hour_production: boolean;
};

export type AddProductionPeriodFormSubmitPayload =
  ProductionPeriodFormSubmitBase & {
    materialId: string;
  };

export type EditProductionPeriodFormSubmitPayload =
  ProductionPeriodFormSubmitBase & {
    materialId?: never;
  };

export type ProductionPeriodFormSubmitPayload =
  | AddProductionPeriodFormSubmitPayload
  | EditProductionPeriodFormSubmitPayload;

export enum ProductionReportMeasurementEntryEnum {
  TAG = 'TAG',
  MANUAL = 'MANUAL',
}

export type ProductionReportMeasurementInput = {
  quantity: number;
  unit: string;
  entry_type: ProductionReportMeasurementEntryEnum;
  tag_id: string | null;
  material_id: string | null;
  routing_id: string | null;
  component_id: string | null;
  timestamp?: string | null;
};

export type ProductionReportMeasurement = {
  id: string;
  quantity: number;
  unit: string;
  entry_type: ProductionReportMeasurementEntryEnum;
  tag_id: string | null;
  material_id: string | null;
  routing_id: string | null;
  component_id: string | null;
  status: ProductionLineStatusEnum;
  response_code: number;
  error_code: string | null;
  retry: number;
};

export type ProductionReport = {
  production_period_id: string;
  status: ProductionLineStatusEnum;
  measurements: ProductionReportMeasurement[];
};

export type ChangeCalculationParams = {
  material: Material | MaterialComponent | MaterialRouting | null;
};

export type TagValueData = {
  value: number;
  unit: string;
  timestamp: string;
  hasError?: boolean;
};

export type ProductionOrderOperationActivities = {
  index: number;
  type: string;
  value: number | null;
  unit: string;
};

export type ProductionOrderItem = {
  productionVersion: string;
};

export type ProductionOrderMaterialItem = {
  number: string;
  actualQuantity: number;
  quantityUnit: string;
  storageLocation: {
    code: string;
  };
  goodsMovementType: string;
  movementIndicator: string | null;
  debitCreditIndicator: string;
  material: {
    code: string;
  };
  alternativeQuantity: {
    quantity: number | null;
    quantityUnit: string | null;
  };
};

export type ProductionOrderOperation = {
  number: string;
  operationQuantity: number | null;
  operationUnit: string | null;
  capacityWorkCenter: {
    code: string;
  };
  activities: ProductionOrderOperationActivities[];
  materialItems: ProductionOrderMaterialItem[];
};

export type ProductionOrder = {
  productionOrder: {
    id: string;
    plannedStart: string;
    scheduledStart: string;
    plannerGroupCode: string;
    alternativeQuantity: {
      quantity: number;
      quantityUnit: string;
    };
    material: {
      code: string;
    };
    plant: {
      code: string;
    };
    productionOrderItems: ProductionOrderItem[];
    productionOrderOperations: ProductionOrderOperation[];
  };
};

export type MaterialUnitQuantity = {
  quantity: string;
  unit: string;
};

export type MaterialUnitQuantities = Record<string, MaterialUnitQuantity>;
